'use client';

import React, { useEffect, useState } from 'react';
import { Impact } from '@/utils/calendar/calendarFormat';
import ImpactDots from '@/components/(blog)/post/calendar/ImpactDots';

export interface UpcomingEvent {
    id: string;
    ts: number;
    dayLabel: string;
    time: string;
    title: string;
    countryFa: string;
    currency: string;
    impact: Impact;
    forecast: string;
}

const faNum = (n: number) => n.toLocaleString('fa-IR');

/** «۲ روز و ۵ ساعت» / «۳ ساعت و ۱۲ دقیقه» / «۸ دقیقه» */
const countdown = (seconds: number): string => {
    const d = Math.floor(seconds / 86400);
    const h = Math.floor((seconds % 86400) / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    if (d > 0) return h ? `${faNum(d)} روز و ${faNum(h)} ساعت` : `${faNum(d)} روز`;
    if (h > 0) return m ? `${faNum(h)} ساعت و ${faNum(m)} دقیقه` : `${faNum(h)} ساعت`;
    return `${faNum(Math.max(1, m))} دقیقه`;
};

/**
 * Sidebar card: the next high-importance event with a countdown, then the rest of today's.
 * The server picks the next event for the HTML; the browser moves on as events pass.
 */
const NextEventCard: React.FC<{ events: UpcomingEvent[]; initialNow: number }> = ({
    events,
    initialNow,
}) => {
    const [now, setNow] = useState(initialNow);
    const [mounted, setMounted] = useState(false);
    useEffect(() => {
        setMounted(true);
        setNow(Math.floor(Date.now() / 1000));
        const timer = setInterval(() => setNow(Math.floor(Date.now() / 1000)), 30_000);
        return () => clearInterval(timer);
    }, []);

    const upcoming = events.filter((e) => e.ts > now);
    const next = upcoming[0];
    const later = upcoming.slice(1, 4);

    return (
        <section
            aria-label='رویداد مهم بعدی'
            className='rounded-2xl border border-gray-100 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-primary-active lg:p-5'
        >
            <p className='text-sm font-medium text-gray-600 dark:text-gray-300'>رویداد مهم بعدی</p>
            {next ? (
                <>
                    <p className='mt-2 text-lg font-black leading-8 text-zinc-900 dark:text-white'>
                        {next.title}
                    </p>
                    <p className='mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-600 dark:text-gray-300'>
                        <span>
                            {next.countryFa}
                            {next.currency && ` · ${next.currency}`}
                        </span>
                        <ImpactDots impact={next.impact} />
                        {next.forecast !== '—' && (
                            <span>
                                پیش‌بینی: <span dir='ltr'>{next.forecast}</span>
                            </span>
                        )}
                    </p>
                    <div className='mt-3 flex items-end justify-between gap-2 rounded-xl bg-gray-50 px-3 py-2.5 dark:bg-white/5'>
                        <span className='flex flex-col'>
                            <span className='text-xs text-gray-500 dark:text-gray-400'>
                                {next.dayLabel}
                            </span>
                            <span className='text-2xl font-black text-zinc-900 dark:text-white'>
                                {next.time}
                            </span>
                        </span>
                        <span
                            suppressHydrationWarning
                            className='text-xs font-medium text-amber-700 dark:text-amber-300'
                        >
                            {mounted ? `${countdown(next.ts - now)} دیگر` : ''}
                        </span>
                    </div>
                    {later.length > 0 && (
                        <ul className='mt-3 flex flex-col divide-y divide-gray-100 dark:divide-white/10'>
                            {later.map((e) => (
                                <li
                                    key={e.id}
                                    className='flex items-center justify-between gap-2 py-2 text-xs'
                                >
                                    <span className='min-w-0 truncate text-zinc-800 dark:text-gray-200'>
                                        {e.title}
                                        <span className='text-gray-400'>
                                            {' '}
                                            · {e.currency || e.countryFa}
                                        </span>
                                    </span>
                                    <span className='shrink-0 text-gray-500 dark:text-gray-400'>
                                        {e.dayLabel.split(' ')[0]} {e.time}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                </>
            ) : (
                <p className='mt-2 text-sm leading-7 text-gray-600 dark:text-gray-300'>
                    رویداد مهم دیگری تا پایان این هفته نمانده است.
                </p>
            )}
            <a
                href='#week-events'
                className='mt-3 block rounded-lg bg-primary px-3 py-2 text-center text-xs font-medium text-white hover:opacity-90 dark:bg-white dark:text-primary'
            >
                همهٔ رویدادهای این هفته
            </a>
        </section>
    );
};

export default NextEventCard;
